import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { MdClose, MdFileDownload, MdPrint } from "react-icons/md";

import {
  downloadBlob,
  openPrintTab,
  printBlob,
} from "../../helpers/print/pdfFile";
import BoltButton from "../UI/Buttons/BoltButton";
import { useTranslation } from "react-i18next";
import { currentLanguage } from "../../i18n";

const KEY = "print:options";
const LEVELS = [
  { id: "beginner", labelKey: "print.beginner", hintKey: "print.beginnerHint" },
  {
    id: "intermediate",
    labelKey: "print.intermediate",
    hintKey: "print.intermediateHint",
  },
];
const LANGUAGES = [
  { id: "en", label: "English" },
  { id: "fr", label: "Français" },
];

const readOptions = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY)) ?? {};
    return {
      level: LEVELS.some((level) => level.id === saved.level)
        ? saved.level
        : "beginner",
      // No saved choice yet: the sheet comes in the language the site is read in.
      lang: LANGUAGES.some((lang) => lang.id === saved.lang)
        ? saved.lang
        : currentLanguage(),
    };
  } catch {
    return { level: "beginner", lang: currentLanguage() };
  }
};

const saveOptions = (options) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(options));
  } catch {
    // The choice just isn't remembered.
  }
};

const PANEL_WIDTH = 320;
const PHONE = 640;

const Choice = ({ name, value, checked, onChange, label, hint }) => (
  <label
    className={`flex cursor-pointer items-start gap-[9px] rounded-[8px] border-[1px] px-[10px] py-[8px] transition-colors ${
      checked
        ? "border-brand bg-brand-light"
        : "border-ui-border bg-ui-surface hover:bg-ui-row-hover"
    }`}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={() => onChange(value)}
      className="mt-[3px] h-[14px] w-[14px] shrink-0 accent-brand"
    />
    <span className="grid gap-[1px]">
      <span className="text-[13px] font-semibold text-ui-text">{label}</span>
      {hint && (
        <span className="text-[11.5px] leading-[1.35] text-ui-text-muted">
          {hint}
        </span>
      )}
    </span>
  </label>
);

/**
 * A Print button that opens the print options: Beginner or Intermediate, and
 * English or French, then Download PDF or Print. `onMake({ level, lang })`
 * makes the PDF and resolves to { blob, filename }. The choices are
 * remembered on this device. On a phone the options open as a sheet from the
 * bottom of the screen; elsewhere they open next to the button.
 */
const PrintMenu = ({
  onMake,
  disabled = false,
  label,
  title,
  variant = "primary",
  size = "sm",
  placement = "up",
  buttonClassName = "",
}) => {
  const { t } = useTranslation("common");
  const shownLabel = label ?? t("print.button");
  const shownTitle = title ?? t("print.title");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(readOptions);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState(null);
  const [place, setPlace] = useState(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const close = useCallback(() => {
    if (busy) return;
    setOpen(false);
    setMessage(null);
  }, [busy]);

  // Where the options go: a sheet on phones; above (or below) the button,
  // inside the screen, elsewhere.
  const measure = useCallback(() => {
    const button = buttonRef.current?.getBoundingClientRect();
    if (!button) return;
    const width = window.innerWidth;
    const height = window.innerHeight;
    if (width < PHONE) {
      setPlace({ phone: true });
      return;
    }
    const panelHeight = panelRef.current?.offsetHeight ?? 380;
    const right = Math.max(
      8,
      Math.min(width - button.right, width - PANEL_WIDTH - 8),
    );
    const roomAbove = button.top - 8;
    const roomBelow = height - button.bottom - 8;
    const up =
      placement === "up"
        ? roomAbove >= panelHeight || roomAbove > roomBelow
        : roomBelow < panelHeight && roomAbove > roomBelow;
    setPlace(
      up
        ? { right, bottom: height - button.top + 8, maxHeight: roomAbove - 8 }
        : { right, top: button.bottom + 8, maxHeight: roomBelow - 8 },
    );
  }, [placement]);

  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure, message, busy]);

  useEffect(() => {
    if (!open) return undefined;
    // Esc closes the options only, not a window they were opened from (it
    // listens on the page after this does).
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      close();
    };
    const onDown = (event) => {
      if (
        panelRef.current?.contains(event.target) ||
        buttonRef.current?.contains(event.target)
      )
        return;
      close();
    };
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open, close, measure]);

  const choose = (change) =>
    setOptions((current) => {
      const next = { ...current, ...change };
      saveOptions(next);
      return next;
    });

  const run = async (action) => {
    setMessage(null);
    // Opened now, on the click: a tab opened after the PDF is made would be
    // blocked as a pop-up.
    const tab = action === "print" ? openPrintTab() : null;
    setBusy(action);
    try {
      const { blob, filename } = await onMake(options);
      if (action === "download") {
        downloadBlob(blob, filename);
        setMessage({
          tone: "good",
          text: t("print.saved", { name: filename }),
        });
      } else {
        const result = await printBlob(blob, { tab, filename });
        if (result === "printed") {
          setOpen(false);
        } else {
          setMessage({
            tone: "good",
            text:
              result === "saved"
                ? t("print.savedToDownloads")
                : t("print.openedInTab"),
          });
        }
      }
    } catch {
      tab?.close?.();
      setMessage({
        tone: "danger",
        text: t("print.pdfFailed"),
      });
    } finally {
      setBusy("");
    }
  };

  const panelStyle = place?.phone
    ? undefined
    : place
      ? {
          width: PANEL_WIDTH,
          right: place.right,
          top: place.top,
          bottom: place.bottom,
          maxHeight: Math.max(220, place.maxHeight),
        }
      : { visibility: "hidden", top: 0, right: 0, width: PANEL_WIDTH };

  return (
    <>
      <span ref={buttonRef} className="inline-flex">
        <BoltButton
          type="button"
          variant={variant}
          size={size}
          icon={MdPrint}
          label={shownLabel}
          onClick={() => (open ? close() : setOpen(true))}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={`shrink-0 whitespace-nowrap ${buttonClassName}`}
        />
      </span>
      {open &&
        createPortal(
          <>
            {place?.phone && (
              <div
                className="fixed inset-0 z-[1400] bg-black/40"
                aria-hidden="true"
                onClick={close}
              />
            )}
            <div
              ref={panelRef}
              role="dialog"
              aria-label={title}
              style={panelStyle}
              className={
                place?.phone
                  ? "fixed inset-x-0 bottom-0 z-[1401] max-h-[88vh] overflow-y-auto rounded-t-[16px] border-t-[1px] border-ui-border bg-ui-surface px-[16px] pb-[calc(16px+env(safe-area-inset-bottom,0px))] pt-[12px] shadow-2xl"
                  : "fixed z-[1401] overflow-y-auto rounded-[12px] border-[1px] border-ui-border bg-ui-surface p-[14px] shadow-2xl"
              }
            >
              {place?.phone && (
                <div className="mx-auto mb-[10px] h-[4px] w-[38px] rounded-full bg-ui-border" />
              )}
              <div className="mb-[10px] flex items-center justify-between gap-[8px]">
                <h2 className="m-0 text-[15px] font-bold text-ui-text">
                  {shownTitle}
                </h2>
                <button
                  type="button"
                  onClick={close}
                  disabled={!!busy}
                  aria-label={t("actions.close")}
                  className="flex h-[30px] w-[30px] items-center justify-center rounded-full border-0 bg-[transparent] text-ui-text-muted hover:bg-ui-row-hover disabled:opacity-50"
                >
                  <MdClose className="h-[18px] w-[18px]" />
                </button>
              </div>
              <fieldset className="mx-0 mb-[12px] mt-0 grid gap-[6px] border-0 p-0">
                <legend className="mb-[6px] p-0 text-[11px] font-bold uppercase tracking-[0.05em] text-ui-text-muted">
                  {t("print.version")}
                </legend>
                {LEVELS.map((level) => (
                  <Choice
                    key={level.id}
                    name="print-level"
                    value={level.id}
                    checked={options.level === level.id}
                    onChange={(value) => choose({ level: value })}
                    label={t(level.labelKey)}
                    hint={t(level.hintKey)}
                  />
                ))}
              </fieldset>
              <fieldset className="mx-0 mb-[14px] mt-0 border-0 p-0">
                <legend className="mb-[6px] p-0 text-[11px] font-bold uppercase tracking-[0.05em] text-ui-text-muted">
                  {t("print.language")}
                </legend>
                <div className="grid grid-cols-2 gap-[6px]">
                  {LANGUAGES.map((lang) => (
                    <Choice
                      key={lang.id}
                      name="print-lang"
                      value={lang.id}
                      checked={options.lang === lang.id}
                      onChange={(value) => choose({ lang: value })}
                      label={lang.label}
                    />
                  ))}
                </div>
              </fieldset>
              {message && (
                <p
                  role="status"
                  className={`mx-0 mb-[10px] mt-0 rounded-[7px] px-[10px] py-[7px] text-[12px] [overflow-wrap:anywhere] ${
                    message.tone === "danger"
                      ? "bg-state-danger-bg text-state-danger-text"
                      : "bg-state-success-bg text-state-success-text"
                  }`}
                >
                  {message.text}
                </p>
              )}
              <div className="grid grid-cols-2 gap-[8px]">
                <BoltButton
                  type="button"
                  variant="outline"
                  size="default"
                  icon={MdFileDownload}
                  label={
                    busy === "download"
                      ? t("print.making")
                      : t("print.downloadPdf")
                  }
                  disabled={!!busy}
                  onClick={() => run("download")}
                  className="w-full justify-center"
                />
                <BoltButton
                  type="button"
                  variant="primary"
                  size="default"
                  icon={MdPrint}
                  label={
                    busy === "print" ? t("print.making") : t("print.print")
                  }
                  disabled={!!busy}
                  onClick={() => run("print")}
                  className="w-full justify-center"
                />
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default PrintMenu;
